import { RoleEnum } from "@/enums/role.enum.ts";
import { SubscriptionModel } from "@/models/subscription.model.ts";
import { UserModel } from "@/models/user.model.ts";
import { getProfile } from "@/services/profile.service.ts";
import { create } from "zustand";
import { persist, PersistOptions } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

interface UserState {
  // State
  isLoggedIn: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isImpersonating: boolean;
  webSocketsUserId: number | null;
  user: UserModel | null;
  impersonatingUser: UserModel | null;

  // Actions
  logout: () => void;
  login: (user: UserModel, token: string) => void;
  updateUser: (user: UserModel) => void;
  getProfile: () => Promise<void>;
  impersonate: (user: UserModel) => void;
  exitImpersonation: () => void;
  updateCredits: (credits: number) => void;
  updateLoading: (isLoading: boolean) => void;

  // Selectors
  getCredits: () => number;
  hasActiveSubscription: () => boolean;
  getActiveSubscriptions: () => SubscriptionModel[] | undefined;
}

type UserPersist = UserState;

const persistOptions: PersistOptions<UserState, UserPersist> = {
  name: "photofuse-store",
};

const useUserStore = create<UserState>()(
  persist(
    immer<UserState>((set, get) => ({
      // State
      isLoggedIn: false,
      isLoading: false,
      isAdmin: false,
      isImpersonating: false,
      user: null,
      impersonatingUser: null,
      webSocketsUserId: null,

      // Actions
      logout: () => {
        set((state) => {
          state.isLoggedIn = false;
          state.isAdmin = false;
          state.user = null;
          state.webSocketsUserId = null;
        });
        localStorage.removeItem("token");
      },

      getProfile: async () => {
        const data = await getProfile();

        const user = data?.data;

        if (user) {
          set((state) => {
            state.isLoggedIn = true;
            state.isAdmin = user.role === RoleEnum.admin;
            state.user = user;
            state.webSocketsUserId = user.id;
          });
        }
      },

      login: (user: UserModel, token: string) => {
        set((state) => {
          state.isLoggedIn = true;
          state.isAdmin = user.role === RoleEnum.admin;
          state.user = user;
          state.webSocketsUserId = user.id;
        });
        localStorage.setItem("token", token);
      },

      updateUser: (user: UserModel) => {
        set((state) => {
          state.isLoggedIn = true;
          state.isAdmin = user.role === RoleEnum.admin;
          state.user = user;
          state.webSocketsUserId = user.id;
        });
      },

      updateCredits: (credits: number) => {
        set((state) => {
          state.user!.credits = credits;
        });
      },

      impersonate: (user: UserModel) => {
        set((state) => {
          state.isImpersonating = true;
          state.impersonatingUser = user;
          state.isAdmin = false;
          state.webSocketsUserId = user.id;
        });
        localStorage.setItem("impersonating_user_id", user.id.toString());
      },

      exitImpersonation: () => {
        set((state) => {
          state.isImpersonating = false;
          state.impersonatingUser = null;
          state.isAdmin = state.user?.role === RoleEnum.admin;
          state.webSocketsUserId = state.user!.id;
        });
        localStorage.removeItem("impersonating_user_id");
      },

      updateLoading: (isLoading: boolean) => {
        set((state) => {
          state.isLoading = isLoading;
        });
      },

      // Selectors
      getCredits: () => {
        const user = get().user;
        return user ? user.credits : 0;
      },

      getActiveSubscriptions: () => {
        const user = get().user;

        if (!user) {
          return;
        }

        const subscriptions = user.subscriptions.data;

        if (!subscriptions) {
          return;
        }

        return subscriptions.filter(
          (subscription) => subscription.status === "active",
        );
      },

      hasActiveSubscription: () => {
        const user = get().user;

        if (!user) {
          return false;
        }

        const subscriptions = user.subscriptions.data;

        if (!subscriptions) {
          return false;
        }

        const activeSubscription = subscriptions.find(
          (subscription) => subscription.status === "active",
        );

        return !!activeSubscription;
      },
    })),
    persistOptions,
  ),
);

export default useUserStore;
